import { AxiosResponse } from "axios";
import { DateFormat, formatDate } from "common";
import dayjs, { Dayjs } from "dayjs";

const LOCKED_STATUS = 423;

export const ACCOUNT_LOCKED_REASON = "account_locked";
export const ACCOUNT_LOCKED_FALLBACK = "Too many failed login attempts.";

interface AccountLockPayload {
    reason?: string;
}

/**
 * A 423 also means a concurrent-edit conflict, so the reason is what identifies a lock.
 */
export const isAccountLockedResponse = (response?: AxiosResponse<unknown> | null) => {
    const data = response?.data as AccountLockPayload | undefined;
    return response?.status === LOCKED_STATUS && data?.reason === ACCOUNT_LOCKED_REASON;
};

const getHeader = (response: AxiosResponse, name: string) => {
    const value = response.headers?.[name];
    return typeof value === "string" || typeof value === "number" ? String(value).trim() : undefined;
};

// Retry-After is delay-seconds (RFC 7231 7.1.3), already ceiled server-side to the minute
// boundary the UI should show. Adding it straight to the device clock is skew-proof: there is
// no server timestamp to compare against, so a device clock that disagrees with the server still
// lands on the right wall-clock minute.
const getUnlockTime = (response: AxiosResponse, now: Dayjs) => {
    const retryAfter = getHeader(response, "retry-after");
    if (!retryAfter || !/^\d+$/.test(retryAfter)) {
        return null;
    }

    const delaySeconds = Number(retryAfter);
    if (delaySeconds <= 0) {
        return null;
    }

    return now.add(delaySeconds, "second");
};

export const getAccountLockMessage = (response: AxiosResponse) => {
    // Read once, so an unlock computed at 23:59:59.9 cannot be labelled against the next day's clock.
    const now = dayjs();
    const unlockAt = getUnlockTime(response, now);
    if (!unlockAt) {
        return ACCOUNT_LOCKED_FALLBACK;
    }

    // The longest rung of the lock ladder is 24h, so no unlock lands beyond tomorrow.
    const day = unlockAt.isSame(now, "day") ? "" : " tomorrow";
    return `${ACCOUNT_LOCKED_FALLBACK} Try again at ${formatDate(unlockAt, DateFormat.TIME)}${day}.`;
};
